import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  addDays,
  dayOfMonth,
  diffDays,
  fmtMonth,
  fmtShort,
  isWeekend,
  todayIn,
  weekdayShort,
  type ISODate,
} from "@shared/dates";
import { plural } from "@shared/format";
import { cn } from "@/lib/cn";
import { AltArrowLeftIcon, AltArrowRightIcon, CalendarIcon } from "./icons";
import { LAYER_ATTR, useLayer } from "./layer";

/*
 * The date picker, in the soft style: rounded tiles on the surface, numerals
 * in Plex Mono, today ringed in deodar, weekends in brass. A range is one
 * tinted band with filled, rounded ends, and it follows the pointer while the
 * second end is being chosen. Keyboard: arrows move, Enter picks, PageUp and
 * PageDown turn the month, Escape drops a half-made range.
 */

const WEEK_START = 1; // Monday

function monthStart(iso: ISODate): ISODate {
  return `${iso.slice(0, 7)}-01`;
}

function addMonths(iso: ISODate, n: number): ISODate {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7)) - 1 + n;
  const date = new Date(Date.UTC(y, m, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

/** Six weeks of days covering the month, padded from the Monday before. */
function gridFor(month: ISODate): ISODate[] {
  const first = monthStart(month);
  const firstDow = new Date(`${first}T00:00:00Z`).getUTCDay();
  const lead = (firstDow - WEEK_START + 7) % 7;
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

const sameMonth = (a: ISODate, b: ISODate) => a.slice(0, 7) === b.slice(0, 7);

/** Nights, closed on the left and open on the right, as the app counts them. */
export type DateRange = { from: ISODate; to: ISODate };

/**
 * How a range is picked. "nights": tap the first night, then the last — a
 * filter over nights. "stay": tap check-in, then check-out — how a desk
 * thinks about a booking. Either way the result is `{ from, to }` with `to`
 * the morning after the last night.
 */
export type RangePick = "nights" | "stay";

export function Calendar({
  value,
  onPick,
  range,
  onPickRange,
  pick = "nights",
  min,
  max,
  timezone,
  months = 1,
  footer,
}: {
  value?: ISODate | null;
  onPick?: (d: ISODate) => void;
  /** The committed span, drawn behind the cursor. Turns on range mode. */
  range?: DateRange | null;
  /** First tap anchors, the second commits. Turns on range mode. */
  onPickRange?: (r: DateRange) => void;
  pick?: RangePick;
  min?: ISODate;
  max?: ISODate;
  timezone?: string;
  /** Two months side by side, for ranges on a wide screen. */
  months?: 1 | 2;
  footer?: ReactNode;
}) {
  const today = todayIn(timezone);
  const start = value ?? range?.from ?? today;
  const [month, setMonth] = useState<ISODate>(monthStart(start));
  const [cursor, setCursor] = useState<ISODate>(start);
  /** Set by the first tap of a range; the second tap ends it. */
  const [anchor, setAnchor] = useState<ISODate | null>(null);
  const shown = useMemo(
    () => Array.from({ length: months }, (_, i) => addMonths(month, i)),
    [month, months],
  );
  const lastShown = shown[shown.length - 1];
  const rangeMode = !!onPickRange;
  const stay = rangeMode && pick === "stay";

  // A date chosen elsewhere — the stepper, a range nav, the other end of a
  // stay — brings the grid to it, unless it is already on screen. Adjusted
  // here rather than in an effect so the jump lands in the same paint.
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    if (value) {
      if (value < month || value >= addMonths(lastShown, 1))
        setMonth(monthStart(value));
      setCursor(value);
    }
  }

  const blocked = (d: ISODate) => !!((min && d < min) || (max && d > max));

  /* While anchored, the span follows the cursor, so the nights light up as
     the pointer crosses them and you can see what you are about to pick. */
  const span = anchor
    ? {
        first: anchor <= cursor ? anchor : cursor,
        last: anchor <= cursor ? cursor : anchor,
      }
    : range
      ? { first: range.from, last: stay ? range.to : addDays(range.to, -1) }
      : null;

  const choose = (d: ISODate) => {
    if (!onPickRange) return onPick?.(d);
    if (!anchor) return setAnchor(d);
    if (stay) {
      // Check-out before check-in starts again from there; the same day is one night.
      if (d < anchor) return setAnchor(d);
      setAnchor(null);
      return onPickRange({
        from: anchor,
        to: d === anchor ? addDays(anchor, 1) : d,
      });
    }
    const first = anchor <= d ? anchor : d;
    const last = anchor <= d ? d : anchor;
    setAnchor(null);
    onPickRange({ from: first, to: addDays(last, 1) });
  };

  const turn = (n: number) => setMonth(addMonths(month, n));

  const move = (delta: number) => {
    const next = addDays(cursor, delta);
    if (blocked(next)) return;
    setCursor(next);
    if (next < month) setMonth(monthStart(next));
    else if (next >= addMonths(lastShown, 1))
      setMonth(addMonths(monthStart(next), -(months - 1)));
  };

  const nights = range ? diffDays(range.from, range.to) : 0;
  const hint = !rangeMode
    ? null
    : anchor
      ? stay
        ? `Check-in ${fmtShort(anchor)} · now tap check-out`
        : `From ${fmtShort(anchor)} · now tap the last night`
      : range
        ? `${fmtShort(range.from)} → ${fmtShort(range.to)} · ${plural(nights, "night")}`
        : stay
          ? "Tap check-in, then check-out"
          : "Tap the first night, then the last";

  return (
    <div
      className="select-none bg-surface"
      onKeyDown={(e) => {
        const keys: Record<string, number> = {
          ArrowLeft: -1,
          ArrowRight: 1,
          ArrowUp: -7,
          ArrowDown: 7,
        };
        if (keys[e.key] !== undefined) {
          e.preventDefault();
          move(keys[e.key]);
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!blocked(cursor)) choose(cursor);
        } else if (e.key === "Escape" && anchor) {
          e.preventDefault();
          e.stopPropagation();
          setAnchor(null);
        } else if (e.key === "PageUp") {
          e.preventDefault();
          turn(-1);
        } else if (e.key === "PageDown") {
          e.preventDefault();
          turn(1);
        }
      }}
    >
      <div
        className="flex"
        role="grid"
        tabIndex={0}
        aria-label={rangeMode ? "Choose dates" : "Choose a date"}
      >
        {shown.map((m, mi) => (
          <div
            key={m}
            className={cn(
              "w-[304px] px-3 pb-2 pt-3",
              mi > 0 && "border-l border-line",
            )}
          >
            <div className="mb-1 flex h-8 items-center justify-between">
              {mi === 0 ? (
                <NavButton
                  label="Previous month"
                  onClick={() => turn(-1)}
                  icon={<AltArrowLeftIcon size={16} />}
                />
              ) : (
                <span className="size-8" />
              )}
              <span className="text-[14px] font-semibold">{fmtMonth(m)}</span>
              {mi === shown.length - 1 ? (
                <NavButton
                  label="Next month"
                  onClick={() => turn(1)}
                  icon={<AltArrowRightIcon size={16} />}
                />
              ) : (
                <span className="size-8" />
              )}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: 7 }, (_, i) =>
                addDays("2026-01-05", i),
              ).map((d) => (
                <span
                  key={d}
                  className={cn(
                    "py-1.5 text-center font-mono text-[10px] uppercase tracking-[.08em]",
                    isWeekend(d) ? "text-brass" : "text-subtle",
                  )}
                >
                  {weekdayShort(d).slice(0, 2)}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {gridFor(m).map((d, i) => {
                const outside = !sameMonth(d, m);
                // Side by side, a day belongs to its own month's grid only.
                if (outside && months > 1)
                  return <span key={d} className="h-10" aria-hidden />;
                return (
                  <DayTile
                    key={d}
                    d={d}
                    col={i % 7}
                    outside={outside}
                    today={d === today}
                    selected={d === value}
                    span={span}
                    off={blocked(d)}
                    cursor={cursor === d}
                    onPick={() => choose(d)}
                    onHover={() => setCursor(d)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {hint && (
        <div
          aria-live="polite"
          className={cn(
            "border-t border-line px-4 py-2.5 text-[13px]",
            anchor ? "font-semibold text-deodar" : "text-muted",
          )}
        >
          {hint}
        </div>
      )}
      {footer}
    </div>
  );
}

function NavButton({
  label,
  onClick,
  icon,
}: {
  label: string;
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex size-8 items-center justify-center rounded-full text-muted hover:bg-sunk hover:text-ink"
    >
      {icon}
    </button>
  );
}

/**
 * One day. The band behind it joins the days of a span into one ribbon;
 * the tile on top carries the number, today's ring and the filled ends.
 */
function DayTile({
  d,
  col,
  outside,
  today,
  selected,
  span,
  off,
  cursor,
  onPick,
  onHover,
}: {
  d: ISODate;
  col: number;
  outside: boolean;
  today: boolean;
  selected: boolean;
  span: { first: ISODate; last: ISODate } | null;
  off: boolean;
  cursor: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  const inSpan = !!span && d >= span.first && d <= span.last;
  const first = !!span && d === span.first;
  const last = !!span && d === span.last;
  const filled = selected || first || last;
  const band = inSpan && span.first !== span.last;
  return (
    <button
      type="button"
      role="gridcell"
      aria-selected={filled || inSpan}
      aria-current={today ? "date" : undefined}
      aria-label={fmtShort(d)}
      disabled={off}
      onClick={onPick}
      onMouseEnter={onHover}
      className={cn(
        "group relative h-10 outline-none",
        off ? "cursor-not-allowed" : "cursor-pointer",
        outside && "opacity-45",
      )}
    >
      {band && (
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-[3px] bg-deodar-tint",
            first ? "left-1/2" : "left-0",
            last ? "right-1/2" : "right-0",
            // A band that wraps to the next week rounds off at the edge of the row.
            col === 0 && !first && "rounded-l-sm",
            col === 6 && !last && "rounded-r-sm",
          )}
        />
      )}
      <span
        className={cn(
          "relative mx-auto flex size-[34px] items-center justify-center rounded-sm font-mono text-[13px] tabular-nums transition-colors duration-100",
          filled
            ? "bg-deodar font-semibold text-on-fill"
            : cn(
                isWeekend(d) ? "text-brass" : "text-ink",
                today &&
                  "font-semibold text-deodar shadow-[inset_0_0_0_1.5px_var(--deodar)]",
                !off && (cursor ? "bg-sunk" : "group-hover:bg-sunk"),
                off && "text-subtle opacity-40",
              ),
          "group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-deodar",
        )}
      >
        {dayOfMonth(d)}
      </span>
    </button>
  );
}

/** Quick picks under a calendar: small rounded chips. */
export function DateChips({
  chips,
}: {
  chips: {
    label: string;
    onPick: () => void;
    on?: boolean;
    disabled?: boolean;
  }[];
}) {
  return (
    <div className="flex flex-wrap gap-1.5 border-t border-line bg-sunk px-3 py-2.5">
      {chips.map((c) => (
        <button
          key={c.label}
          type="button"
          disabled={c.disabled}
          onClick={c.onPick}
          className={cn(
            "h-8 rounded-full border px-3 text-[13px] font-semibold disabled:opacity-40",
            c.on
              ? "border-deodar bg-deodar-tint text-ink"
              : "border-line-strong bg-surface text-muted hover:text-ink",
          )}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A date as a button; tapping it opens the calendar underneath. Replaces the
 * browser's own picker so the whole app reads in one voice.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  label,
  timezone,
  className,
  align = "left",
  compact,
  icon = true,
}: {
  value: ISODate;
  onChange: (d: ISODate) => void;
  min?: ISODate;
  max?: ISODate;
  label?: string;
  timezone?: string;
  className?: string;
  align?: "left" | "right";
  compact?: boolean;
  icon?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const today = todayIn(timezone);
  const close = useCallback(() => setOpen(false), []);
  // In <body>, so a Dialog, Sheet or rounded card around the field can't cut the calendar off.
  useLayer(open, ref, layerRef, close, { align });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const relative =
    value === today
      ? "Today"
      : value === addDays(today, 1)
        ? "Tomorrow"
        : diffDays(today, value) === -1
          ? "Yesterday"
          : null;
  const pickAndClose = (d: ISODate) => {
    onChange(d);
    setOpen(false);
  };
  const outOfBounds = (d: ISODate) => !!((min && d < min) || (max && d > max));

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 rounded-md border border-line-strong bg-sunk px-3 text-left hover:bg-canvas aria-expanded:border-deodar aria-expanded:bg-surface aria-expanded:ring-soft",
          compact ? "min-h-[42px]" : "min-h-[48px]",
        )}
      >
        {icon && (
          <CalendarIcon
            size={compact ? 16 : 18}
            className="shrink-0 text-subtle"
          />
        )}
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          {label && (
            <span className="font-mono text-[9px] uppercase tracking-[.12em] text-subtle">
              {label}
            </span>
          )}
          <span className="truncate font-mono text-[14px] font-semibold">
            {fmtShort(value)}
          </span>
        </span>
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[.1em] text-subtle">
          {relative ?? weekdayShort(value)}
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={layerRef}
            {...{ [LAYER_ATTR]: "" }}
            role="dialog"
            aria-label={label ?? "Pick a date"}
            className="animate-pop-k invisible fixed left-0 top-0 z-[90] overflow-y-auto rounded-lg border border-line-strong bg-surface shadow-float"
          >
            <Calendar
              value={value}
              min={min}
              max={max}
              timezone={timezone}
              onPick={pickAndClose}
              footer={
                <DateChips
                  chips={[
                    { label: "Yesterday", d: addDays(today, -1) },
                    { label: "Today", d: today },
                    { label: "Tomorrow", d: addDays(today, 1) },
                  ].map((c) => ({
                    label: c.label,
                    on: value === c.d,
                    disabled: outOfBounds(c.d),
                    onPick: () => pickAndClose(c.d),
                  }))}
                />
              }
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
